import { BASE_URL } from "@/constants/endpoints";
import { createAxiosWithAuth } from "@/utils/axios-config";
import { objectToQueryStringWithArray } from "@/utils/query-string";
import type { AxiosInstance } from "axios";

let zonalGrossMarginApiPromise: Promise<AxiosInstance> | null = null;

const getZonalGrossMarginApi = async (): Promise<AxiosInstance | null> => {
  if (zonalGrossMarginApiPromise !== null) return zonalGrossMarginApiPromise;
  zonalGrossMarginApiPromise = createAxiosWithAuth(BASE_URL, "jwt");
  return zonalGrossMarginApiPromise;
};

export interface CreateZonalGrossMarginBody {
  idGranoProductivo: number;
  idZonaProductiva: number;
  idCampanaProductiva: number;
  cotizacionDolar: number;
  precioCereal: number;
  rindeEstimadoZonal: number;
  precioAgroquimicos: number;
  precioFertilizantes: number;
  precioSemillas: number;
  precioLabores: number;
  precioSeguros: number;
  costoImplantacion: number;
  precioAlquiler?: number;
  gastoDeCosecha: number;
  inversionTotal: number;
  equilibrio: number;
  id?: number;
}

export interface ZonalGrossMarginDetail {
  id: number;
  idGranoProductivo?: number;
  idZonaProductiva?: number;
  idCampProd?: number;
  cotizacionDolar?: number;
  precioCereal?: number;
  rindeEstimadoZonal?: number;
  precioAgroquimicos?: number;
  precioFertilizantes?: number;
  precioSemillas?: number;
  precioLabores?: number;
  precioSeguros?: number;
  costoImplantacion?: number;
  precioAlquiler?: number;
  gastoDeCosecha?: number;
  inversionTotal?: number;
  equilibrio?: number;
}

export interface ZonalGrossMarginListItem {
  id: number;
  cultivos: string;
  zonaProductiva: string;
  campana: string;
  equilibrio: number;
  rindeEstimado: number;
}

export interface ZonalGrossMarginListResponse {
  listado: ZonalGrossMarginListItem[];
  totalPages: number;
  totalRecords: number;
}

export const createZonalGrossMargin = async (
  body: CreateZonalGrossMarginBody,
) => {
  const api = await getZonalGrossMarginApi();
  if (!api) return;

  const response = await api.post("/MargenBrutoZonal", body);
  return response.data;
};

export interface GetZonalGrossMarginListProps {
  "P.Pagina": number;
  "P.Records": number;
  GranoProductivo?: number[];
  ZonaProductiva?: number[];
  CampanaProductiva?: number[];
  Equilibrio?: number;
  RindeEstimado?: number;
}

export const getZonalGrossMarginList = async (
  params: GetZonalGrossMarginListProps,
): Promise<ZonalGrossMarginListResponse | undefined> => {
  const api = await getZonalGrossMarginApi();
  if (!api) return;

  const queryString = objectToQueryStringWithArray(params);
  const response = await api.get(`/MargenBrutoZonal/All?${queryString}`);
  return response.data;
};

export const getZonalGrossMarginDetail = async (
  id: number,
): Promise<ZonalGrossMarginDetail | undefined> => {
  const api = await getZonalGrossMarginApi();
  if (!api) return;

  const response = await api.get(`/MargenBrutoZonal/${id}`);
  return response.data;
};

export const updateZonalGrossMargin = async (
  id: number,
  body: CreateZonalGrossMarginBody,
) => {
  const api = await getZonalGrossMarginApi();
  if (!api) return;

  const response = await api.put(`/MargenBrutoZonal/${id}`, body);
  return response.data;
};

export interface GetZonalGrossMarginByCultivoProps {
  IdGranoProductivo: number;
  IdZonaProductiva: number;
  IdCampProd: number;
}

export const getZonalGrossMarginByCultivo = async (
  params: GetZonalGrossMarginByCultivoProps,
): Promise<ZonalGrossMarginDetail | null> => {
  const api = await getZonalGrossMarginApi();
  if (!api) return null;

  const response = await api.get(
    `/MargenBrutoZonal?${objectToQueryStringWithArray(params)}`,
  );
  return response.data ?? null;
};